import { inject, Injectable } from '@angular/core';
import { FlashMessageService, FlashMessageType } from '@libs/shared/common';
import { AppErrorModel } from '@libs/shared/models';
import { ShopErrorCollectionModel, ShopErrorItemModel } from '@libs/shop/models';
import { SHOP_ERROR_MAP } from './shop-error-map';

@Injectable({ providedIn: 'root' })
export class ShopErrorParserService {
  // these are English only generic error codes which we do not want to show to the user
  private readonly shopGenericInternalErrorCodes: string[] = ['113', '114', '103', '4005'];
  private readonly flashMessageService: FlashMessageService = inject(FlashMessageService);

  public tryParseErrors(error: unknown): ShopErrorItemModel[] {
    if (!error) {
      return [];
    }

    const appError: AppErrorModel<ShopErrorCollectionModel> = error as AppErrorModel<ShopErrorCollectionModel>;
    const errorCollection: ShopErrorCollectionModel | undefined = appError?.data;

    if (errorCollection?.errors) {
      return errorCollection.errors;
    }

    return [];
  }

  public tryParseKnownErrors(error: unknown): ShopErrorItemModel[] {
    const errors: ShopErrorItemModel[] = this.tryParseErrors(error);
    const knownErrors: ShopErrorItemModel[] = [];

    for (const error of errors) {
      if (!error.internalErrorCode) {
        continue;
      }

      const knownErrorMessage: string | undefined = SHOP_ERROR_MAP[error.internalErrorCode];

      if (!knownErrorMessage) {
        continue;
      }

      knownErrors.push({
        ...error,
        message: knownErrorMessage,
      });
    }

    return knownErrors;
  }

  public tryParseShopLlErrors(error: unknown): ShopErrorItemModel[] {
    return this.tryParseErrors(error).filter(x => x.internalErrorCode?.toLowerCase().startsWith('ll'));
  }

  public parseAndShowRawErrorMessages(error: unknown, unknownErrorKey: FlashMessageType): void {
    const shopErrors: ShopErrorItemModel[] = this.tryParseErrors(error).filter(x => !this.shopGenericInternalErrorCodes.includes(x.internalErrorCode ?? ''));

    if (!shopErrors.length) {
      this.flashMessageService.showMessage(unknownErrorKey);

      return;
    }

    for (const shopError of shopErrors) {
      this.flashMessageService.showCustomMessage({
        message: shopError.message,
        theme: 'error',
        duration: 10000,
      });
    }
  }
}
